import { useMemo, useContext } from 'react';
import { TradingContext } from '../contexts/TradingContext';
import usePositionCalculations from './usePositionCalculations';
import { flatBookPnL, futuresLiveTodayPnL, isDirectTradestationFuturesAccount } from '../utils/accountUtils';

// Single source of truth for empty metrics
const EMPTY_METRICS = {
  accountValue: 0,
  todayChange: 0,
  todayUnrealizedChange: 0,
  todayChangePercent: 0,
  todayUnrealizedChangePercent: 0,
  realizedPL: 0,
  unrealizedPL: 0,
  unrealizedPLPercent: 0,
  cashBalance: 0,
  marketValue: 0,
  buyingPower: 0,
  bodBalance: 0,
  isReady: false,
  isFuturesAccount: false,
  isDirectTradestationFutures: false,
};

/**
 * Check if account is futures type
 * See src/mock_data/accountDataExamples.js for data structure differences
 * See src/utils/accountUtils.js for shared utility functions
 */
const isFuturesAccountType = (balance) => {
  return balance?.AccountType === 'Futures';
};

/**
 * Real-time account metrics hook.
 * 
 * Single source of truth for:
 * - Account Value (updates in real-time from positions stream)
 * - Today's Change (total daily P&L for loss limits — unrealized + realized)
 * - Today's Unrealized Change (account summary cards — unrealized daily only)
 * - Unrealized P&L (updates in real-time from positions stream)
 * - Realized P&L (updates when balances refetch)
 * 
 * IMPORTANT - Today's Change calculation:
 * - FUTURES (TradeStation Direct only): realized P&L + live open P&L from
 *   positions, adjusted by the broker snapshot's today-vs-open gap so the day
 *   figure ticks with quotes. See futuresLiveTodayPnL.
 * - FUTURES (Connect Trade): balance.TodaysProfitLoss + realized P&L. CT
 *   streams its own day P&L and must not use the Direct formula.
 * - EQUITIES/EQUITY: Σ(position.TodaysProfitLoss) + BalanceDetail.RealizedProfitLoss
 *   (sums today's P&L from all positions + today's realized P&L)
 * - FALLBACK: Try position.TodaysProfitLoss + realizedPL, otherwise balance.TodaysProfitLoss
 * 
 * CRITICAL REQUIREMENT: todayChange MUST always be: today's unrealized P&L + today's realized P&L
 * This ensures max daily loss progress shows the correct total daily P&L.
 * This calculation is used for daily loss limit tracking, so it must match the backend exactly.
 */
export const useRealtimeAccountMetrics = () => {
  const { balances, positions, selectedAccount, positionsHydrated } = useContext(TradingContext);
  const { calculateUnrealizedPL, calculateMarketValue } = usePositionCalculations();
  
  // Create a signature that changes when position values change
  // This ensures useMemo recalculates when Last price, UPL, or TodaysProfitLoss changes
  const accountPositionsForSignature = positions?.filter(p => p.AccountID === selectedAccount?.AccountID) || [];
  const positionsSignature = accountPositionsForSignature
    .map(p => `${p.PositionKey || p.PositionID}:${p.Quantity}:${p.Last}:${p.MarkToMarketPrice || ''}:${p.UnrealizedProfitLoss}:${p.TodaysProfitLoss || ''}`)
    .join('|');
  
  // Calculate metrics directly (no refs, no effects - just pure calculation)
  return useMemo(() => {
    // Return empty if prerequisites not met
    if (!balances || balances.length === 0 || !selectedAccount?.AccountID) {
      return EMPTY_METRICS;
    }
    
    // Find the balance for the selected account
    const balance = balances.find(b => b.AccountID === selectedAccount.AccountID);
    if (!balance) {
      // No balance for selected account - data is stale from account switch
      return { ...EMPTY_METRICS, isReady: false };
    }
    
    // Determine account type - affects Today's Change calculation
    // See src/mock_data/accountDataExamples.js for detailed differences
    const accountType = balance.AccountType;
    const isFutures = accountType === 'Futures';
    const isDirectTsFutures = isDirectTradestationFuturesAccount(selectedAccount, balance);
    const isEquities = accountType === 'Equities' || accountType === 'Equity';
    
    // Filter to selected account only (positions are cleared on switch; this is a safety net)
    const accountPositions = positions?.filter(p => p.AccountID === selectedAccount.AccountID) || [];
    
    // Parse balance values
    const equity = parseFloat(balance.Equity) || 0;
    const cashBalance = parseFloat(balance.CashBalance) || 0;
    const buyingPower = parseFloat(balance.BuyingPower) || 0;
    const realizedPL = parseFloat(balance.BalanceDetail?.RealizedProfitLoss) || 0;
    const balanceUnrealizedPL = parseFloat(balance.BalanceDetail?.UnrealizedProfitLoss) || 0;
    const balanceTodayPL = parseFloat(balance.TodaysProfitLoss) || 0;
    
    // Calculate BOD Balance directly (no ref storage needed)
    // BOD Balance = Equity - Realized P&L - Unrealized P&L
    const bodBalance = equity - realizedPL - balanceUnrealizedPL;
    
    // Book is flat after a close: do not keep the last balance snapshot's open P&L.
    // Before the first positions snapshot, still fall through to the balance fields.
    if (accountPositions.length === 0 && positionsHydrated) {
      const flat = flatBookPnL({
        realizedPL,
        balanceTodayPL: balance.TodaysProfitLoss,
        balanceUnrealizedPL: balance.BalanceDetail?.UnrealizedProfitLoss,
        isDirectTradestationFutures: isDirectTsFutures,
        isFutures,
      });
      const todayChangePercent = bodBalance > 0 ? (flat.todayChange / bodBalance) * 100 : 0;
      const todayUnrealizedChangePercent = bodBalance > 0 ? (flat.todayUnrealizedChange / bodBalance) * 100 : 0;
      return {
        accountValue: equity,
        todayChange: flat.todayChange,
        todayUnrealizedChange: flat.todayUnrealizedChange,
        todayChangePercent,
        todayUnrealizedChangePercent,
        realizedPL: flat.realizedPL,
        unrealizedPL: 0,
        unrealizedPLPercent: 0,
        cashBalance,
        marketValue: 0,
        buyingPower,
        bodBalance,
        isReady: true,
        isFuturesAccount: isFutures,
        isDirectTradestationFutures: isDirectTsFutures,
      };
    }

    // If no matching positions yet (streams/REST still warming), use balance fields only
    if (accountPositions.length === 0) {
      const todayUnrealizedChange = Number.isFinite(balanceTodayPL) ? balanceTodayPL : 0;
      const todayChange = isFutures
        ? todayUnrealizedChange + realizedPL
        : todayUnrealizedChange + realizedPL;
      const todayChangePercent = bodBalance > 0 ? (todayChange / bodBalance) * 100 : 0;
      const todayUnrealizedChangePercent = bodBalance > 0 ? (todayUnrealizedChange / bodBalance) * 100 : 0;
      const openPL = Number.isFinite(balanceUnrealizedPL) ? balanceUnrealizedPL : 0;
      
      return {
        accountValue: equity,
        todayChange,
        todayUnrealizedChange,
        todayChangePercent,
        todayUnrealizedChangePercent,
        realizedPL,
        unrealizedPL: openPL,
        unrealizedPLPercent: bodBalance > 0 ? (openPL / bodBalance) * 100 : 0,
        cashBalance,
        marketValue: 0,
        buyingPower,
        bodBalance,
        isReady: true,
        isFuturesAccount: isFutures,
        isDirectTradestationFutures: isDirectTsFutures,
      };
    }
    
    // Calculate real-time unrealized P&L from positions (for Open P&L display)
    let totalUnrealizedPL = 0;
    let totalMarketValue = 0;
    accountPositions.forEach(position => {
      const unrealizedPL = calculateUnrealizedPL(position);
      totalUnrealizedPL += unrealizedPL;
      totalMarketValue += calculateMarketValue(position);
    });
    
    // Today's unrealized daily change (account summary cards)
    let todayUnrealizedChange;
    let futuresToday = null;
    if (isDirectTsFutures) {
      futuresToday = futuresLiveTodayPnL({
        realizedPL,
        balanceTodayPL: balance.TodaysProfitLoss,
        balanceUnrealizedPL: balance.BalanceDetail?.UnrealizedProfitLoss,
        liveOpenPL: totalUnrealizedPL,
      });
      todayUnrealizedChange = futuresToday.todayUnrealizedChange;
    } else if (isFutures) {
      todayUnrealizedChange = balanceTodayPL !== undefined &&
        balanceTodayPL !== null &&
        balanceTodayPL !== ''
        ? balanceTodayPL
        : 0;
    } else if (isEquities) {
      let positionsTodayPnL = 0;
      accountPositions.forEach((position) => {
        if (position.TodaysProfitLoss !== undefined &&
            position.TodaysProfitLoss !== null &&
            position.TodaysProfitLoss !== '') {
          positionsTodayPnL += parseFloat(position.TodaysProfitLoss || 0);
        }
      });
      todayUnrealizedChange = positionsTodayPnL;
    } else {
      let positionsTodayPnL = 0;
      let hasPositionTodayPnL = false;
      accountPositions.forEach((position) => {
        if (position.TodaysProfitLoss !== undefined &&
            position.TodaysProfitLoss !== null &&
            position.TodaysProfitLoss !== '') {
          positionsTodayPnL += parseFloat(position.TodaysProfitLoss || 0);
          hasPositionTodayPnL = true;
        }
      });
      if (hasPositionTodayPnL) {
        todayUnrealizedChange = positionsTodayPnL;
      } else if (balanceTodayPL !== undefined &&
                 balanceTodayPL !== null &&
                 balanceTodayPL !== '') {
        todayUnrealizedChange = balanceTodayPL;
      } else {
        todayUnrealizedChange = 0;
      }
    }

    // Total daily P&L (unrealized + realized) — used for loss limits / display
    let todayChange;
    if (isDirectTsFutures) {
      todayChange = futuresToday.todayChange;
    } else if (isEquities) {
      todayChange = todayUnrealizedChange + realizedPL;
    } else {
      todayChange = todayUnrealizedChange + realizedPL;
    }
    
    const todayChangePercent = bodBalance > 0 ? (todayChange / bodBalance) * 100 : 0;
    const todayUnrealizedChangePercent = bodBalance > 0 ? (todayUnrealizedChange / bodBalance) * 100 : 0;
    const unrealizedPLPercent = bodBalance > 0 ? (totalUnrealizedPL / bodBalance) * 100 : 0;
    
    // Some Connect Trade brokers (Tastytrade) report equity as cash only.
    // When that happens, account value is cash plus open position market value.
    const equityOmitsPositions = totalMarketValue > 0
      && Math.abs(equity - cashBalance) < Math.max(1, Math.abs(totalMarketValue) * 0.05);
    const accountValue = equityOmitsPositions ? cashBalance + totalMarketValue : equity;

    return {
      accountValue,
      todayChange,
      todayUnrealizedChange,
      todayChangePercent,
      todayUnrealizedChangePercent,
      realizedPL,
      unrealizedPL: totalUnrealizedPL,
      unrealizedPLPercent,
      cashBalance,
      marketValue: totalMarketValue,
      buyingPower,
      bodBalance,
      isReady: true,
      isFuturesAccount: isFutures,
      isDirectTradestationFutures: isDirectTsFutures,
    };
  }, [
    balances,
    positions,
    positionsSignature,
    positionsHydrated,
    selectedAccount?.AccountID,
    calculateUnrealizedPL,
    calculateMarketValue
  ]);
};
